import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Layers,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldQuestion,
  Trash2,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError, humaniseKey, type AssessmentTopicPublic } from "@eduviaos/shared";
import { AssessmentTopicEditorSheet } from "@/components/assessments/AssessmentTopicEditorSheet";
import { useArchiveAssessmentTopic, useAssessmentTopics } from "@/hooks/useAssessments";
import { useAuth } from "@/auth/AuthProvider";
import { useCanAuthorAssessments } from "@/hooks/useAssessmentAuthoring";
import { TenantAuthoringPausedBanner } from "@/components/assessments/TenantAuthoringPausedBanner";

/**
 * Phase H5 FE-1 — IA Assessments → Topics.
 *
 * DataTable CRUD over `/assessments/topics`. Iron Rule #16 compliant:
 * sticky header in scrollable region, no zebra stripes, action column
 * last with `…` dropdown, status pill for archived state.
 *
 * Permission: `manage_question_bank` on the BE (defaults: admin + teacher).
 * Read-only viewers (e.g. SA without the perm) get a banner explaining
 * the gate and the Action column is hidden from rows.
 */

export function AssessmentTopicsPage() {
  const [qInput, setQInput] = React.useState("");
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingTopic, setEditingTopic] = React.useState<AssessmentTopicPublic | null>(null);
  const [pendingArchive, setPendingArchive] = React.useState<AssessmentTopicPublic | null>(null);
  const [archiveReason, setArchiveReason] = React.useState("");
  const [archiveError, setArchiveError] = React.useState<string | null>(null);

  const { data, isLoading, isFetching, refetch, isError, error } = useAssessmentTopics({
    includeArchived,
    search: qInput.trim() || undefined,
  });
  const archiveMutation = useArchiveAssessmentTopic();

  const { user } = useAuth();
  // Phase H5d — RBAC (`manage_question_bank`) + per-tenant
  // `assessments_authoring_by_tenant` flag, with `pausedByTenant`
  // exposed so the banner can disambiguate from the perm-missing case.
  const { canAuthor: canWrite, missingRbac, pausedByTenant } = useCanAuthorAssessments();

  const items = data?.items ?? [];
  const topicNameBySlug = new Map(items.map((t) => [t.slug, t.name]));
  const total = data?.total ?? 0;

  const handleConfirmArchive = async () => {
    if (!pendingArchive) return;
    setArchiveError(null);
    try {
      await archiveMutation.mutateAsync({
        slug: pendingArchive.slug,
        reason: archiveReason.trim() || undefined,
      });
      toast.success(`Archived "${pendingArchive.name}".`);
      setPendingArchive(null);
      setArchiveReason("");
    } catch (raw) {
      const message =
        raw instanceof ApiError && raw.message
          ? raw.message
          : raw instanceof Error && raw.message
            ? raw.message
            : "Could not archive — please try again.";
      setArchiveError(message);
    }
  };

  return (
    <div className="mx-auto max-w-6xl py-8" data-testid="topics-catalogue">
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/assessments">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Assessments
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Topics</PageHeaderHeading>
            <PageHeaderDescription>
              Taxonomy used to scope every question and every blueprint item.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              aria-label="Refresh topics"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canWrite ? (
              <Button
                size="sm"
                onClick={() => {
                  setEditingTopic(null);
                  setEditorOpen(true);
                }}
                data-testid="topics-create"
              >
                <Plus aria-hidden="true" className="size-4" />
                New topic
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac && user ? (
        <div
          role="status"
          className="border-border/60 bg-muted/40 text-muted-foreground mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <ShieldQuestion
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
          />
          <span className="flex-1">
            Read-only view. Authoring topics needs the{" "}
            <code className="text-foreground">manage_question_bank</code> permission.
          </span>
        </div>
      ) : null}

      {pausedByTenant ? <TenantAuthoringPausedBanner className="mt-4" /> : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load topics
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Filter by name…"
              className="pl-8"
              aria-label="Filter topics"
              data-testid="topics-filter"
            />
          </div>
          {qInput ? (
            <Button variant="ghost" size="sm" onClick={() => setQInput("")}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <div className="flex items-center gap-2 pl-2">
            <Checkbox
              id="topics-include-archived"
              checked={includeArchived}
              onCheckedChange={(v) => setIncludeArchived(v === true)}
            />
            <Label
              htmlFor="topics-include-archived"
              className="text-muted-foreground text-xs font-normal"
            >
              Show archived
            </Label>
          </div>
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[36%]">Name</TableHead>
                <TableHead className="w-[20%]">Subject</TableHead>
                <TableHead className="w-[20%]">Parent</TableHead>
                <TableHead className="w-[10%]">Grades</TableHead>
                <TableHead className="w-[9%]">Status</TableHead>
                {canWrite ? <TableHead className="w-[5%] text-right">Action</TableHead> : null}
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={canWrite ? 6 : 5} />
            ) : (
              <TableBody>
                {items.map((topic) => (
                  <TopicRow
                    key={topic.id}
                    topic={topic}
                    parentName={
                      topic.parent_slug
                        ? (topicNameBySlug.get(topic.parent_slug) ??
                          humaniseKey(topic.parent_slug, "—"))
                        : null
                    }
                    canWrite={canWrite}
                    onEdit={(t) => {
                      setEditingTopic(t);
                      setEditorOpen(true);
                    }}
                    onArchive={(t) => {
                      setArchiveError(null);
                      setArchiveReason("");
                      setPendingArchive(t);
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<Layers aria-hidden="true" />}
            title={qInput ? "No matches" : "No topics yet"}
            description={
              qInput
                ? "Try a different filter or clear it to see every topic."
                : "Create a topic to start tagging questions. Templates draw their questions from these topics."
            }
            action={
              qInput ? (
                <Button variant="outline" size="sm" onClick={() => setQInput("")}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filter
                </Button>
              ) : canWrite ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingTopic(null);
                    setEditorOpen(true);
                  }}
                >
                  <Plus aria-hidden="true" className="size-4" />
                  New topic
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && total > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              {items.length.toLocaleString()} shown
              {qInput ? " (filtered)" : ""}
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">How it's used:</strong> every question and every
          blueprint item points at one or more topics. To replace a topic, archive it and create a
          new one — templates that already use the old topic keep working.
        </CardContent>
      </Card>

      <AssessmentTopicEditorSheet
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditingTopic(null);
        }}
        existing={editingTopic}
        onSaved={(saved) => {
          toast.success(
            editingTopic ? `Saved changes to "${saved.name}".` : `Created "${saved.name}".`,
          );
        }}
      />

      <Dialog
        open={Boolean(pendingArchive)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingArchive(null);
            setArchiveError(null);
            setArchiveReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {pendingArchive?.name}?</DialogTitle>
            <DialogDescription>
              The topic disappears from new authoring flows. Existing questions and templates that
              use it stay valid — archived topics are filtered out only when drawing fresh blueprint
              candidates. Reason is optional but recommended for compliance.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="archive-reason">Reason (optional, 3–500 chars)</Label>
            <Textarea
              id="archive-reason"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Superseded by a more granular taxonomy."
              maxLength={500}
              rows={3}
            />
          </div>
          {archiveError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {archiveError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={archiveMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmArchive()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                <>
                  <Trash2 aria-hidden="true" className="size-4" />
                  Archive topic
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface TopicRowProps {
  topic: AssessmentTopicPublic;
  /** Display name of the parent topic; `null` for top-level topics. */
  parentName: string | null;
  canWrite: boolean;
  onEdit: (t: AssessmentTopicPublic) => void;
  onArchive: (t: AssessmentTopicPublic) => void;
}

function TopicRow({ topic, parentName, canWrite, onEdit, onArchive }: TopicRowProps) {
  const isArchived = topic.deleted_at !== null;
  return (
    <TableRow>
      <TableCell>
        <div className="grid">
          <span className="text-foreground font-medium">{topic.name}</span>
          {topic.description ? (
            <span className="text-muted-foreground line-clamp-1 text-xs">{topic.description}</span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="text-sm">{topic.subject}</TableCell>
      <TableCell className="text-muted-foreground text-sm">{parentName ?? "—"}</TableCell>
      <TableCell className="text-sm tabular-nums">
        {topic.grade_band ? `${topic.grade_band.min}–${topic.grade_band.max}` : "Any"}
      </TableCell>
      <TableCell>
        <StatusPill status={isArchived ? "archived" : "active"} />
      </TableCell>
      {canWrite ? (
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label={`Open actions menu for ${topic.name}`}
              >
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onEdit(topic)} disabled={isArchived}>
                <Pencil aria-hidden="true" className="size-3.5" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => onArchive(topic)}
                disabled={isArchived}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 aria-hidden="true" className="size-3.5" />
                Archive…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      ) : null}
    </TableRow>
  );
}
